import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Pencil, Trash2, Moon, RotateCcw, X } from 'lucide-react';
import { listRoles, createRole, updateRole, deleteRole, resetDefaultRoles } from '@/api/mafia';
import { toast } from 'sonner';
import { useTranslation } from '@/i18n/LanguageContext';

const TEAM_LABELS = {
  red:   { key: 'roles.civilians', color: 'text-rose-400',  bg: 'bg-rose-500/10', border: 'border-rose-500/30' },
  black: { key: 'roles.mafia',     color: 'text-zinc-200',  bg: 'bg-zinc-800/40', border: 'border-zinc-500/40' },
  gray:  { key: 'roles.neutral',   color: 'text-stone-300', bg: 'bg-stone-500/10', border: 'border-stone-400/30' },
};

const EMPTY_ROLE = {
  name: '',
  team: 'red',
  icon: '🎭',
  description: '',
  abilities: [],
  night_action: false,
  night_order: 100,
  max_count: 0,
  killable_night: true,
  killable_day: true,
  wakes_nights: null,
};

export default function MafiaRolesPage() {
 const navigate = useNavigate();
 const { t, tRole, tRoleDesc } = useTranslation();
 const [roles, setRoles] = useState([]);
 const [loading, setLoading] = useState(true);
 const [editing, setEditing] = useState(null); // role object or null
 const [showCreate, setShowCreate] = useState(false);

 const load = async () => {
 setLoading(true);
 try {
 const data = await listRoles();
 setRoles(data.roles || []);
 } catch (e) {
 toast.error('Failed to load roles');
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => { load(); }, []);

 const handleDelete = async (id) => {
 if (!window.confirm('Delete this role?')) return;
 try {
 await deleteRole(id);
 toast.success('Role deleted');
 load();
 } catch (e) {
 toast.error('Failed to delete');
 }
 };

 const handleReset = async () => {
 if (!window.confirm('Reset default roles? Custom roles will not be touched.')) return;
 try {
 await resetDefaultRoles();
 toast.success('Defaults restored');
 load();
 } catch (e) {
 toast.error('Failed to reset');
 }
 };

 const grouped = { red: [], black: [], gray: [] };
 roles.forEach((r) => { if (grouped[r.team]) grouped[r.team].push(r); });

 return (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className="min-h-screen pt-16 pb-32 px-6 max-w-5xl mx-auto"
 data-testid="mafia-roles-page"
 >
 <button onClick={() => navigate('/mafia')} className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#DC2626] mb-6 transition-colors">
 <ArrowLeft size={16} /> {t('common.back')}
 </button>

 <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
 <div>
 <h1 className="font-serif text-3xl md:text-4xl text-[#F5F5F5]">{t('roles.title')}</h1>
 <p className="text-[#A3A3A3] mt-2 text-sm">{t('roles.subtitle')}</p>
 </div>
 <div className="flex gap-2">
 <button onClick={handleReset} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 text-[#A3A3A3] hover:text-[#F5F5F5] hover:border-[#DC2626]/40 text-sm">
 <RotateCcw size={14} /> {t('roles.reset')}
 </button>
 <button onClick={() => setShowCreate(true)} data-testid="mafia-create-role-btn" className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium text-sm bg-[#DC2626] hover:bg-[#B91C1C] transition-colors">
 <Plus size={14} /> {t('roles.new')}
 </button>
 </div>
 </div>

 {loading && <p className="text-[#A3A3A3]">{t('common.loading')}</p>}

 {!loading && Object.entries(grouped).map(([team, list]) => (
 <section key={team} className="mb-10">
 <h2 className={`font-serif text-xl mb-4 ${TEAM_LABELS[team].color}`}>
 {t(TEAM_LABELS[team].key)} <span className="text-[#A3A3A3] text-sm font-sans">({list.length})</span>
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 {list.map((r) => (
 <div
 key={r.id}
 className={` border ${TEAM_LABELS[team].border} ${TEAM_LABELS[team].bg} p-4`}
 >
 <div className="flex items-start justify-between gap-3">
 <div className="flex items-start gap-3 min-w-0">
 <div className="text-2xl">{r.icon}</div>
 <div className="min-w-0">
 <h3 className="text-[#F5F5F5] font-medium truncate">{tRole(r.name)}</h3>
 <p className="text-xs text-[#A3A3A3] mt-0.5 flex items-center gap-2">
 {r.night_action && <span className="inline-flex items-center gap-1"><Moon size={11} /> {t('roles.night-order')} {r.night_order}</span>}
 {r.max_count > 0 && <span>{t('roles.max')} {r.max_count}</span>}
 {r.is_default && <span style={{ color: '#DC2626' }}>{t('roles.default')}</span>}
 </p>
 </div>
 </div>
 <div className="flex gap-1">
 <button onClick={() => setEditing(r)} className="p-2 rounded-lg hover:bg-white/5 text-[#A3A3A3] hover:text-[#F5F5F5]">
 <Pencil size={14} />
 </button>
 <button onClick={() => handleDelete(r.id)} className="p-2 rounded-lg hover:bg-white/5 text-[#A3A3A3] hover:text-rose-400">
 <Trash2 size={14} />
 </button>
 </div>
 </div>
 <p className="text-xs text-[#A3A3A3] mt-3 leading-relaxed">{tRoleDesc(r.name, r.description)}</p>
 {r.abilities?.length > 0 && (
 <ul className="text-xs text-[#A3A3A3] mt-2 space-y-1">
 {r.abilities.map((a, i) => (
 <li key={i} className="flex gap-2"><span style={{ color: '#DC2626' }}>·</span>{a}</li>
 ))}
 </ul>
 )}
 </div>
 ))}
 </div>
 </section>
 ))}

 {(editing || showCreate) && (
 <RoleEditor
 initial={editing || EMPTY_ROLE}
 isNew={!editing}
 onClose={() => { setEditing(null); setShowCreate(false); }}
 onSaved={() => { setEditing(null); setShowCreate(false); load(); }}
 />
 )}
 </motion.div>
 );
}

function RoleEditor({ initial, isNew, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial.name || '',
    team: initial.team || 'red',
    icon: initial.icon || '🎭',
    description: initial.description || '',
    abilities: initial.abilities || [],
    actions: initial.actions || [],
    night_action: !!initial.night_action,
    day_action: !!initial.day_action,
    night_order: initial.night_order ?? 100,
    max_count: initial.max_count ?? 0,
    killable_night: initial.killable_night ?? true,
    killable_day: initial.killable_day ?? true,
    wakes_nights: initial.wakes_nights ?? null,  // null = every night
    can_kill: initial.can_kill ?? false,
    can_view_role: initial.can_view_role ?? false,
    can_steal_role: initial.can_steal_role ?? false,
    can_lie: initial.can_lie ?? false,
    can_bite: initial.can_bite ?? false,
    can_give_excuse: initial.can_give_excuse ?? false,
    excuse_self_max: initial.excuse_self_max ?? 6,
    can_plant_mafia: initial.can_plant_mafia ?? false,
    can_block_role: initial.can_block_role ?? false,
    can_heal: initial.can_heal ?? false,
    can_give_immunity: initial.can_give_immunity ?? false,
    can_track: initial.can_track ?? false,
    track_max: initial.track_max ?? 5,
    can_plant_bomb: initial.can_plant_bomb ?? false,
    bomb_max: initial.bomb_max ?? 5,
    can_copy_dead_role: initial.can_copy_dead_role ?? false,
    copy_role_from_night: initial.copy_role_from_night ?? 3,
    can_nominate_vote: initial.can_nominate_vote ?? false,
    nominate_max: initial.nominate_max ?? 3,
    wake_on_even_nights: initial.wake_on_even_nights ?? false,
    can_give_eternal_life: initial.can_give_eternal_life ?? false,
    eternal_life_max: initial.eternal_life_max ?? 2,
    has_eternal_life: initial.has_eternal_life ?? false,
    is_tie_breaker: initial.is_tie_breaker ?? false,
    takes_lives_on_death: initial.takes_lives_on_death ?? false,
    takes_lives_count: initial.takes_lives_count ?? 1,
    can_use_ability: initial.can_use_ability ?? null,
  });
  const [abilityInput, setAbilityInput] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // If night_action off, force wakes_nights to [] (never wakes)
      if (!payload.night_action) payload.wakes_nights = [];
      if (isNew) {
        await createRole(payload);
        toast.success('Role created');
      } else {
        await updateRole(initial.id, payload);
        toast.success('Role updated');
      }
      onSaved();
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Wake-nights preset detection
  const wakeMode = (() => {
    const w = form.wakes_nights;
    if (w === null || w === undefined) return 'all';
    if (Array.isArray(w) && w.length === 0) return 'none';
    const arr = [...w].sort((a, b) => a - b).join(',');
    if (arr === '1,3,5,6') return 'odd_late';
    if (arr === '2,4,6') return 'even';
    return 'custom';
  })();

  const setWakeMode = (mode) => {
    if (mode === 'all') setForm({ ...form, wakes_nights: null });
    else if (mode === 'none') setForm({ ...form, wakes_nights: [] });
    else if (mode === 'odd_late') setForm({ ...form, wakes_nights: [1, 3, 5, 6] });
    else if (mode === 'even') setForm({ ...form, wakes_nights: [2, 4, 6] });
    else setForm({ ...form, wakes_nights: form.wakes_nights || [1] }); // custom — keep or seed
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg max-h-[92vh] overflow-y-auto ms-card"
        style={{ background: 'var(--ms-surface-1)' }}
        data-testid="role-editor-modal"
      >
        <div className="px-6 py-4 ms-border-b flex items-center justify-between">
          <div>
            <p className="ms-kicker ms-kicker-red">{isNew ? 'Create role' : 'Edit role'}</p>
            <h2 className="ms-display text-xl mt-0.5">{form.name || 'Untitled role'}</h2>
          </div>
          <button onClick={onClose} className="ms-btn ms-btn-ghost ms-btn-sm" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Identity */}
          <div className="flex items-center gap-3">
            <input
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
              maxLength={4}
              className="ms-input text-3xl text-center"
              style={{ width: '4rem', padding: '0.5rem 0' }}
            />
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Role name"
              data-testid="role-form-name"
              className="ms-input flex-1 text-base"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium ms-text">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="ms-input resize-none"
              placeholder="Short rule blurb shown to the host…"
            />
          </div>

          {/* Abilities */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium ms-text">Abilities</label>
            <div className="flex gap-2">
              <input
                value={abilityInput}
                onChange={(e) => setAbilityInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && abilityInput.trim()) {
                    setForm({ ...form, abilities: [...form.abilities, abilityInput.trim()] });
                    setAbilityInput('');
                  }
                }}
                placeholder="Press Enter to add"
                className="ms-input flex-1"
              />
              <button
                onClick={() => { if (abilityInput.trim()) { setForm({ ...form, abilities: [...form.abilities, abilityInput.trim()] }); setAbilityInput(''); } }}
                className="ms-btn ms-btn-secondary ms-btn-sm"
              >
                Add
              </button>
            </div>
            {form.abilities.length > 0 && (
              <ul className="space-y-1 pt-1">
                {form.abilities.map((a, i) => (
                  <li key={i} className="flex items-center justify-between text-sm ms-text-2 px-3 py-1.5"
                      style={{ background: 'var(--ms-surface-2)', borderRadius: 'var(--ms-radius-sm)' }}>
                    <span>{a}</span>
                    <button
                      onClick={() => setForm({ ...form, abilities: form.abilities.filter((_, idx) => idx !== i) })}
                      className="text-rose-400 hover:text-rose-300"
                    >
                      <X size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Action buttons (shown in the night/day action panel) */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium ms-text">Action buttons</label>
            <p className="text-[11px] ms-text-2">Buttons shown to the host in this role's action panel during the game (e.g., Убить, Иммунитет, Проверить).</p>
            <ActionChipsEditor
              value={form.actions}
              onChange={(actions) => setForm({ ...form, actions })}
            />
          </div>

          {/* Phase actions */}
          <div className="ms-border" style={{ borderRadius: 'var(--ms-radius-md)' }}>
            <SwitchRow
              label="Has night action"
              hint="Does this role do something during the night phase?"
              value={form.night_action}
              onChange={(v) => setForm({ ...form, night_action: v, wakes_nights: v ? form.wakes_nights : [] })}
              testid="role-toggle-night-action"
            />
            <div className="ms-border-t">
              <SwitchRow
                label="Has day action"
                hint="Does this role do something during the day phase (e.g. Шахид взрыв, Политик speech)?"
                value={form.day_action}
                onChange={(v) => setForm({ ...form, day_action: v })}
                testid="role-toggle-day-action"
              />
            </div>
          </div>

          {/* Wake nights schedule (only when night_action) */}
          {form.night_action && (
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <label className="text-xs font-medium ms-text">Wakes up on nights</label>
                <span className="text-[11px] ms-text-3">
                  {wakeMode === 'all' && 'every night'}
                  {wakeMode === 'none' && 'never wakes'}
                  {(wakeMode === 'odd_late' || wakeMode === 'even' || wakeMode === 'custom') && (form.wakes_nights || []).join(', ')}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 ms-border p-1" style={{ borderRadius: 'var(--ms-radius-md)' }}>
                {[
                  { k: 'all', l: 'All nights' },
                  { k: 'odd_late', l: '1, 3, 5, 6' },
                  { k: 'even', l: '2, 4, 6' },
                  { k: 'custom', l: 'Custom' },
                ].map(opt => (
                  <button
                    key={opt.k}
                    type="button"
                    onClick={() => setWakeMode(opt.k)}
                    data-testid={`wake-mode-${opt.k}`}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${wakeMode === opt.k ? 'ms-text' : 'ms-text-2 hover:ms-text'}`}
                    style={wakeMode === opt.k ? { background: 'var(--ms-surface-3)', borderRadius: 'var(--ms-radius-sm)' } : { borderRadius: 'var(--ms-radius-sm)' }}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
              {wakeMode === 'custom' && (
                <input
                  value={(form.wakes_nights || []).join(', ')}
                  onChange={(e) => {
                    const nums = e.target.value.split(',').map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n) && n >= 1 && n <= 30);
                    setForm({ ...form, wakes_nights: nums });
                  }}
                  placeholder="e.g. 1, 3, 5, 7"
                  className="ms-input text-sm"
                  data-testid="wake-custom-input"
                />
              )}
            </div>
          )}

          {/* Capabilities */}
          <div className="ms-border" style={{ borderRadius: 'var(--ms-radius-md)' }}>
            <SwitchRow
              label="Can kill"
              hint="May eliminate another player at night."
              value={form.can_kill}
              onChange={(v) => setForm({ ...form, can_kill: v })}
              testid="role-toggle-can-kill"
            />
            <div className="ms-border-t">
              <SwitchRow
                label="Can view chosen player's role at night"
                hint="Reveals the role of one target each night (e.g., Дон, Свидетель)."
                value={form.can_view_role}
                onChange={(v) => setForm({ ...form, can_view_role: v })}
                testid="role-toggle-can-view"
              />
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Can steal player's role at night"
                hint="Steals the night ability of a chosen target (e.g., Вор)."
                value={form.can_steal_role}
                onChange={(v) => setForm({ ...form, can_steal_role: v })}
                testid="role-toggle-can-steal"
              />
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Может оболгать (Can lie)"
                hint="Лжец: оболганный игрок ночью при проверке другой роли определяется как противоположная команда (красный ↔ чёрный)."
                value={form.can_lie}
                onChange={(v) => setForm({ ...form, can_lie: v })}
                testid="role-toggle-can-lie"
              />
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Может укусить (Can bite)"
                hint="Оборотень: укушенный спасается один раз от ночного убийства. Если линчуют днём — на следующее утро встаёт как Оборотень."
                value={form.can_bite}
                onChange={(v) => setForm({ ...form, can_bite: v })}
                testid="role-toggle-can-bite"
              />
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Может дать отмазку (Can give excuse)"
                hint="Адвокат: даёт цели иммунитет от дневного линча на следующий день (не спасает от бомбы Шахида). Себе — не более N раз за игру."
                value={form.can_give_excuse}
                onChange={(v) => setForm({ ...form, can_give_excuse: v })}
                testid="role-toggle-can-give-excuse"
              />
              {form.can_give_excuse && (
                <div className="px-4 pb-3 flex items-center justify-between gap-4">
                  <p className="text-xs ms-text-2">Сколько раз можно дать отмазку себе</p>
                  <input
                    type="number"
                    min={0}
                    max={20}
                    value={form.excuse_self_max}
                    onChange={(e) => setForm({ ...form, excuse_self_max: Math.max(0, parseInt(e.target.value || 0, 10)) })}
                    data-testid="role-excuse-self-max"
                    className="ms-input text-right tabular-nums"
                    style={{ width: '5rem' }}
                  />
                </div>
              )}
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Может подсадить (Can plant mafia)"
                hint="Дилер: подсаженный игрок начиная с третьей ночи становится мафией и просыпается с командой."
                value={form.can_plant_mafia}
                onChange={(v) => setForm({ ...form, can_plant_mafia: v })}
                testid="role-toggle-can-plant"
              />
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Может заблокировать (Can block role)"
                hint="Путана: блокирует ночное действие цели. Комиссар видит её как красную, пока вся мафия жива."
                value={form.can_block_role}
                onChange={(v) => setForm({ ...form, can_block_role: v })}
                testid="role-toggle-can-block"
              />
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Может лечить (Can heal)"
                hint="Доктор: лечит цель от ночного убийства."
                value={form.can_heal}
                onChange={(v) => setForm({ ...form, can_heal: v })}
                testid="role-toggle-can-heal"
              />
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Может дать иммунитет (Can give immunity)"
                hint="Исповедник: цель получает иммунитет от Маньяка на текущую ночь."
                value={form.can_give_immunity}
                onChange={(v) => setForm({ ...form, can_give_immunity: v })}
                testid="role-toggle-can-give-immunity"
              />
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Может следить (Can track)"
                hint="Свидетель: ведёт слежку за N игроками одновременно (max)."
                value={form.can_track}
                onChange={(v) => setForm({ ...form, can_track: v })}
                testid="role-toggle-can-track"
              />
              {form.can_track && (
                <div className="px-4 pb-3 flex items-center justify-between gap-4">
                  <p className="text-xs ms-text-2">Максимум целей для слежки</p>
                  <input
                    type="number" min={1} max={20}
                    value={form.track_max}
                    onChange={(e) => setForm({ ...form, track_max: Math.max(1, parseInt(e.target.value || 1, 10)) })}
                    data-testid="role-track-max"
                    className="ms-input text-right tabular-nums"
                    style={{ width: '5rem' }}
                  />
                </div>
              )}
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Может заложить бомбу (Can plant bomb)"
                hint="Шахид: бомбы на N игроков. При линчевании Шахида все заминированные взрываются вместе с ним."
                value={form.can_plant_bomb}
                onChange={(v) => setForm({ ...form, can_plant_bomb: v })}
                testid="role-toggle-can-plant-bomb"
              />
              {form.can_plant_bomb && (
                <div className="px-4 pb-3 flex items-center justify-between gap-4">
                  <p className="text-xs ms-text-2">Максимум бомб</p>
                  <input
                    type="number" min={1} max={20}
                    value={form.bomb_max}
                    onChange={(e) => setForm({ ...form, bomb_max: Math.max(1, parseInt(e.target.value || 1, 10)) })}
                    data-testid="role-bomb-max"
                    className="ms-input text-right tabular-nums"
                    style={{ width: '5rem' }}
                  />
                </div>
              )}
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Может скопировать роль мёртвого (Can copy dead role)"
                hint="Хамелеон: выбирает выпавшую роль и играет за неё до конца партии."
                value={form.can_copy_dead_role}
                onChange={(v) => setForm({ ...form, can_copy_dead_role: v })}
                testid="role-toggle-can-copy-dead-role"
              />
              {form.can_copy_dead_role && (
                <div className="px-4 pb-3 flex items-center justify-between gap-4">
                  <p className="text-xs ms-text-2">С какой ночи можно копировать</p>
                  <input
                    type="number" min={1} max={20}
                    value={form.copy_role_from_night}
                    onChange={(e) => setForm({ ...form, copy_role_from_night: Math.max(1, parseInt(e.target.value || 1, 10)) })}
                    data-testid="role-copy-from-night"
                    className="ms-input text-right tabular-nums"
                    style={{ width: '5rem' }}
                  />
                </div>
              )}
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Может выставить на голосование (Can nominate)"
                hint="Политик: выставляет N игроков на дневное голосование (или 1 игрока ×N = автоматическая смерть)."
                value={form.can_nominate_vote}
                onChange={(v) => setForm({ ...form, can_nominate_vote: v })}
                testid="role-toggle-can-nominate"
              />
              {form.can_nominate_vote && (
                <div className="px-4 pb-3 flex items-center justify-between gap-4">
                  <p className="text-xs ms-text-2">Максимум номинаций</p>
                  <input
                    type="number" min={1} max={10}
                    value={form.nominate_max}
                    onChange={(e) => setForm({ ...form, nominate_max: Math.max(1, parseInt(e.target.value || 1, 10)) })}
                    data-testid="role-nominate-max"
                    className="ms-input text-right tabular-nums"
                    style={{ width: '5rem' }}
                  />
                </div>
              )}
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Просыпается только на чётных ночах"
                hint="Только ночи 2, 4, 6, 8 ... (например, Политик)."
                value={form.wake_on_even_nights}
                onChange={(v) => setForm({ ...form, wake_on_even_nights: v })}
                testid="role-toggle-wake-even"
              />
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Может дать вечную жизнь (Can give eternal life)"
                hint="Некромант: делает N других игроков бессмертными."
                value={form.can_give_eternal_life}
                onChange={(v) => setForm({ ...form, can_give_eternal_life: v })}
                testid="role-toggle-can-give-eternal-life"
              />
              {form.can_give_eternal_life && (
                <div className="px-4 pb-3 flex items-center justify-between gap-4">
                  <p className="text-xs ms-text-2">Сколько целей получают вечную жизнь</p>
                  <input
                    type="number" min={1} max={10}
                    value={form.eternal_life_max}
                    onChange={(e) => setForm({ ...form, eternal_life_max: Math.max(1, parseInt(e.target.value || 1, 10)) })}
                    data-testid="role-eternal-life-max"
                    className="ms-input text-right tabular-nums"
                    style={{ width: '5rem' }}
                  />
                </div>
              )}
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Вечная жизнь (Has eternal life)"
                hint="Игрок не может быть убит ни ночью, ни днём (например, Пьяница)."
                value={form.has_eternal_life}
                onChange={(v) => setForm({ ...form, has_eternal_life: v, killable_night: v ? false : form.killable_night, killable_day: v ? false : form.killable_day })}
                testid="role-toggle-eternal-life"
              />
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Разрешает ничью при голосовании (Tie-breaker)"
                hint="Палач: при равенстве голосов выбирает, кого казнить — или казнит всех."
                value={form.is_tie_breaker}
                onChange={(v) => setForm({ ...form, is_tie_breaker: v })}
                testid="role-toggle-tie-breaker"
              />
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Takes other lives when killed"
                hint="On death, takes N players with them (e.g., Шахид, Палач)."
                value={form.takes_lives_on_death}
                onChange={(v) => setForm({ ...form, takes_lives_on_death: v, takes_lives_count: v ? Math.max(form.takes_lives_count || 1, 1) : 0 })}
                testid="role-toggle-takes-lives"
              />
              {form.takes_lives_on_death && (
                <div className="px-4 pb-3 flex items-center justify-between gap-4">
                  <p className="text-xs ms-text-2">How many lives</p>
                  <input
                    type="number"
                    min={1}
                    max={5}
                    value={form.takes_lives_count}
                    onChange={(e) => setForm({ ...form, takes_lives_count: Math.max(1, parseInt(e.target.value || 1, 10)) })}
                    data-testid="role-takes-lives-count"
                    className="ms-input text-right tabular-nums"
                    style={{ width: '5rem' }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Ability schedule */}
          <NightScheduleSelector
            label="Can use ability on nights"
            value={form.can_use_ability}
            onChange={(v) => setForm({ ...form, can_use_ability: v })}
            testidPrefix="ability"
          />

          {/* Killability */}
          <div className="ms-border" style={{ borderRadius: 'var(--ms-radius-md)' }}>
            <SwitchRow
              label="Can be killed at night"
              hint="Mafia shots / maniac etc. can take this role out at night."
              value={form.killable_night}
              onChange={(v) => setForm({ ...form, killable_night: v })}
              testid="role-toggle-killable-night"
            />
            <div className="ms-border-t">
              <SwitchRow
                label="Can be killed at day"
                hint="Can be eliminated by daytime voting / lynch."
                value={form.killable_day}
                onChange={(v) => setForm({ ...form, killable_day: v })}
                testid="role-toggle-killable-day"
              />
            </div>
            <div className="ms-border-t">
              <SwitchRow
                label="Бессмертный"
                hint="Cannot be killed by any normal means. Overrides both toggles above."
                value={!form.killable_night && !form.killable_day}
                onChange={(v) => setForm({ ...form, killable_night: !v, killable_day: !v })}
                testid="role-toggle-immortal"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 ms-border-t flex gap-2">
          <button onClick={onClose} className="ms-btn ms-btn-secondary flex-1">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="role-form-save" className="ms-btn ms-btn-primary flex-1">
            {saving ? 'Saving…' : 'Save role'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function NightScheduleSelector({ label, value, onChange, testidPrefix = 'sched' }) {
  // value: null=all, []=none, [1,3..]=custom
  const mode = (() => {
    if (value === null || value === undefined) return 'all';
    if (Array.isArray(value) && value.length === 0) return 'none';
    const arr = [...value].sort((a, b) => a - b).join(',');
    if (arr === '1,3,5,6') return 'odd_late';
    if (arr === '2,4,6') return 'even';
    return 'custom';
  })();
  const setMode = (m) => {
    if (m === 'all') onChange(null);
    else if (m === 'none') onChange([]);
    else if (m === 'odd_late') onChange([1, 3, 5, 6]);
    else if (m === 'even') onChange([2, 4, 6]);
    else onChange(value || [1]);
  };
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-medium ms-text">{label}</label>
        <span className="text-[11px] ms-text-3">
          {mode === 'all' && 'every night'}
          {mode === 'none' && 'never'}
          {(mode === 'odd_late' || mode === 'even' || mode === 'custom') && (value || []).join(', ')}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 ms-border p-1" style={{ borderRadius: 'var(--ms-radius-md)' }}>
        {[
          { k: 'all', l: 'All nights' },
          { k: 'odd_late', l: '1, 3, 5, 6' },
          { k: 'even', l: '2, 4, 6' },
          { k: 'custom', l: 'Custom' },
        ].map(opt => (
          <button
            key={opt.k}
            type="button"
            onClick={() => setMode(opt.k)}
            data-testid={`${testidPrefix}-mode-${opt.k}`}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${mode === opt.k ? 'ms-text' : 'ms-text-2 hover:ms-text'}`}
            style={mode === opt.k ? { background: 'var(--ms-surface-3)', borderRadius: 'var(--ms-radius-sm)' } : { borderRadius: 'var(--ms-radius-sm)' }}
          >
            {opt.l}
          </button>
        ))}
      </div>
      {mode === 'custom' && (
        <input
          value={(value || []).join(', ')}
          onChange={(e) => {
            const nums = e.target.value.split(',').map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n) && n >= 1 && n <= 30);
            onChange(nums);
          }}
          placeholder="e.g. 1, 3, 5, 7"
          className="ms-input text-sm"
          data-testid={`${testidPrefix}-custom-input`}
        />
      )}
    </div>
  );
}

function SwitchRow({ label, hint, value, onChange, testid }) {
  return (
    <div className="px-4 py-3 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm ms-text font-medium">{label}</p>
        {hint && <p className="text-xs ms-text-2 mt-0.5 leading-snug">{hint}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        data-testid={testid}
        className="shrink-0 w-11 h-6 flex items-center transition-colors"
        style={{
          background: value ? 'var(--ms-red)' : 'var(--ms-surface-3)',
          borderRadius: '999px',
          padding: '2px',
          justifyContent: value ? 'flex-end' : 'flex-start',
        }}
      >
        <span className="w-4 h-4 bg-white block" style={{ borderRadius: '999px' }} />
      </button>
    </div>
  );
}

function ActionChipsEditor({ value, onChange }) {
  const [draft, setDraft] = useState('');
  const PRESETS = ['Убить', 'Иммунитет', 'Проверить', 'Лечить', 'Заблокировать'];
  const add = (label) => {
    const v = (label || '').trim();
    if (!v) return;
    if (value.includes(v)) return;
    onChange([...value, v]);
  };
  return (
    <div className="space-y-2" data-testid="role-actions-editor">
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { add(draft); setDraft(''); } }}
          placeholder="Custom action label…"
          className="ms-input flex-1"
          data-testid="role-action-input"
        />
        <button type="button" onClick={() => { add(draft); setDraft(''); }} className="ms-btn ms-btn-secondary ms-btn-sm">
          Add
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.filter(p => !value.includes(p)).map(p => (
          <button
            key={p}
            type="button"
            onClick={() => add(p)}
            data-testid={`role-action-preset-${p}`}
            className="ms-chip hover:ms-chip-red transition-colors"
            style={{ cursor: 'pointer' }}
          >
            + {p}
          </button>
        ))}
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {value.map((a, i) => (
            <span key={i} className="ms-chip ms-chip-red" data-testid={`role-action-chip-${a}`}>
              {a}
              <button
                type="button"
                onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                className="ml-1 hover:text-white"
                aria-label="Remove"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
